import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Form, FormControl, FormItem, FormLabel, FormMessage } from "../../components/ui/form";
import toast from "react-hot-toast";
import ImageWithBasePath from "../../core/data/img/ImageWithBasePath";
import { useStore } from "../../app/stores/store";
import { observer } from "mobx-react-lite";
import { all_routes } from "../router/all_routes";
import { registerSchemaForEmail, RegisterSchemaForEmail } from "../../lib/schemas/registerSchema";
import { Loader2, Mail, User, ArrowLeft } from "lucide-react";
import Header from "../common/header";
import Footer from "../common/footer";

const routes = all_routes;

const debounce = (func: (...args: any[]) => void, delay: number) => {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
};

const CustomerRegister = () => {
  const { userStore } = useStore();
  const navigate = useNavigate();

  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [email, setEmail] = useState("");
  const [emailExists, setEmailExists] = useState<boolean | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const lastCheckedEmailRef = useRef<string | null>(null);

  const form = useForm<RegisterSchemaForEmail>({
    resolver: zodResolver(registerSchemaForEmail),
    mode: "onTouched",
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    watch,
  } = form;

  const checkEmail = async (value: string) => {
    if (!value.includes("@")) {
      setEmailExists(null);
      return;
    }

    if (lastCheckedEmailRef.current === value) return;

    const MIN_VISIBLE_TIME = 500;
    const start = Date.now();
    setCheckingEmail(true);

    try {
      const exists = await userStore.checkUserExistsByEmail(value);
      setEmailExists(exists);
      lastCheckedEmailRef.current = value;

      if (exists && !otpSent) {
        setEmail(value);
        const result = await userStore.registerWithEmail({
          displayName: "",
          email: value,
        });
        if (result.status === "success") {
          setOtpSent(true);
          toast.success("OTP sent to your email.");
        } else {
          toast.error("Error: " + result.error);
        }
      }
    } catch (error) {
      console.error("Error checking email:", error);
      toast.error("Failed to check email.");
      setEmailExists(null);
    } finally {
      const elapsed = Date.now() - start;
      const remaining = MIN_VISIBLE_TIME - elapsed;
      setTimeout(() => {
        setCheckingEmail(false);
      }, remaining > 0 ? remaining : 0);
    }
  };

  const debouncedCheckEmail = debounce(checkEmail, 600);

  useEffect(() => {
    const subscription = watch((value) => {
      if (value.email) {
        debouncedCheckEmail(value.email);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const onSubmit = async (data: RegisterSchemaForEmail) => {
    const result = await userStore.registerWithEmail(data);
    if (result.status === "success") {
      setEmail(data.email);
      setOtpSent(true);
      toast.success("OTP sent to your email.");
    } else {
      toast.error("Error: " + result.error);
    }
  };

  const handleOtpSubmit = async () => {
    const result = await userStore.login({ email, password: otp });
    if (result.status === "success") {
      toast.success("Logged in successfully.");
      navigate("/");
    } else {
      toast.error("Error: " + result.error);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Header />
      <main className="flex-grow pt-24 md:pt-32 flex items-center justify-center py-8">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Image */}
            <div className="hidden lg:block relative h-[600px] rounded-xl overflow-hidden">
              <img
                src="https://picsum.photos/seed/loginbg/800/600"
                alt="Abstract background image with colorful shapes and app screenshots"
                className="w-full h-full object-cover rounded-xl"
                loading="eager"
                data-ai-hint="colorful abstract background"
              />
            </div>

            {/* Right Side - Registration Form */}
            <div className="flex items-center justify-center">
              <div className="w-full max-w-md">
                <Card className="border-0 ">
                  <CardHeader className="text-center pb-4">
                    <div className="flex justify-between items-center mb-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(routes.login)}
                        className="flex items-center gap-2"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                      </Button>
                      <ImageWithBasePath
                        lazyLoad={true}
                        src="assets/img/new updated logo-01.png"
                        alt="Logo"
                        className="h-10"
                      />
                      <div className="w-12"></div>
                    </div>
                    <CardTitle className="text-2xl font-bold text-gray-900">
                      Customer Registration
                    </CardTitle>
                    <p className="text-gray-600 mt-2">
                      Enter your email to receive an OTP
                    </p>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <Form {...form}>
                      {otpSent ? (
                        <>
                          <FormItem>
                            <FormLabel>Enter OTP</FormLabel>
                            <Input
                              value={otp}
                              onChange={(e) => setOtp(e.target.value)}
                              placeholder="Enter the OTP sent to your email"
                            />
                          </FormItem>
                          <Button
                            onClick={handleOtpSubmit}
                            disabled={!otp}
                            className="w-full bg-primary hover:bg-primary/90 text-white py-6"
                          >
                            Verify OTP & Login
                          </Button>
                        </>
                      ) : (
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                          {emailExists === false && (
                            <FormItem>
                              <FormLabel>Display Name</FormLabel>
                              <div className="relative">
                                {/* <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" /> */}
                                <Input
                                  {...register("displayName")}
                                  placeholder="Your display name"
                                  className="pl-9 focus-visible:ring-[#e71875] focus-visible:border-[#e71875]"
                                />
                              </div>
                              <FormMessage className="text-red-500" />
                            </FormItem>
                          )}

                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <div className="relative">
                              {/* <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" /> */}
                              <Input
                                type="email"
                                {...register("email")}
                                placeholder="you@example.com"
                                className="pl-9 focus-visible:ring-[#e71875] focus-visible:border-[#e71875]"
                              />
                            </div>
                            <FormMessage className="text-red-500" />
                            {checkingEmail && (
                              <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Checking email availability...
                              </div>
                            )}
                          </FormItem>

                          {emailExists === false && (
                            <Button
                              type="submit"
                              disabled={!isValid || isSubmitting}
                              className="w-full bg-primary hover:bg-primary/90 text-white py-6"
                            >
                              {isSubmitting ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Registering...
                                </>
                              ) : (
                                "Register & Send OTP"
                              )}
                            </Button>
                          )}
                        </form>
                      )}
                    </Form>

                    <div className="text-center pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-600">
                        Already have an account?{" "}
                        <Link
                          to={routes.login}
                          className="text-primary hover:text-primary/80 font-medium"
                        >
                          Sign In
                        </Link>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default observer(CustomerRegister);